package com.example.socialmediaapp.ui.components

import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.ExpandLess
import androidx.compose.material.icons.filled.ExpandMore
import androidx.compose.material.icons.filled.Search
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.example.socialmediaapp.data.models.Tag

// ============================================
// UNIVERSAL TAGS (50 tags across all categories)
// ============================================

val UNIVERSAL_TAGS = listOf(
    // Technology
    Tag("tech-software", "Software Development", "💻", "Technology", "#4A90E2"),
    Tag("tech-hardware", "Hardware & Gadgets", "🔧", "Technology", "#4A90E2"),
    Tag("tech-ai", "AI & Machine Learning", "🤖", "Technology", "#4A90E2"),
    Tag("tech-gaming", "Gaming", "🎮", "Technology", "#4A90E2"),
    Tag("tech-crypto", "Crypto & Blockchain", "₿", "Technology", "#4A90E2"),
    Tag("tech-cybersecurity", "Cybersecurity", "🔒", "Technology", "#4A90E2"),
    Tag("tech-web3", "Web3 & Decentralized", "🌐", "Technology", "#4A90E2"),
    Tag("tech-mobile", "Mobile Development", "📱", "Technology", "#4A90E2"),
    // Business
    Tag("biz-entrepreneurship", "Entrepreneurship", "🚀", "Business", "#2ECC71"),
    Tag("biz-investing", "Investing & Stocks", "📈", "Business", "#2ECC71"),
    Tag("biz-marketing", "Marketing & Sales", "📣", "Business", "#2ECC71"),
    Tag("biz-finance", "Personal Finance", "💰", "Business", "#2ECC71"),
    Tag("biz-realestate", "Real Estate", "🏠", "Business", "#2ECC71"),
    Tag("biz-freelance", "Freelancing & Gigs", "💼", "Business", "#2ECC71"),
    // Health
    Tag("health-fitness", "Fitness & Exercise", "💪", "Health", "#E74C3C"),
    Tag("health-nutrition", "Nutrition & Diet", "🥗", "Health", "#E74C3C"),
    Tag("health-mental", "Mental Health", "🧠", "Health", "#E74C3C"),
    Tag("health-wellness", "Wellness & Self-Care", "🧘", "Health", "#E74C3C"),
    Tag("health-medical", "Medical & Healthcare", "⚕️", "Health", "#E74C3C"),
    // Arts
    Tag("art-visual", "Visual Arts", "🎨", "Arts", "#9B59B6"),
    Tag("art-music", "Music", "🎵", "Arts", "#9B59B6"),
    Tag("art-writing", "Writing & Literature", "✍️", "Arts", "#9B59B6"),
    Tag("art-photography", "Photography", "📷", "Arts", "#9B59B6"),
    Tag("art-design", "Design & UX", "🎯", "Arts", "#9B59B6"),
    Tag("art-film", "Film & Video", "🎬", "Arts", "#9B59B6"),
    // Education
    Tag("edu-learning", "Learning & Education", "📚", "Education", "#F39C12"),
    Tag("edu-career", "Career Development", "🎓", "Education", "#F39C12"),
    Tag("edu-productivity", "Productivity", "⚡", "Education", "#F39C12"),
    Tag("edu-science", "Science & Research", "🔬", "Education", "#F39C12"),
    // Lifestyle
    Tag("life-travel", "Travel & Adventure", "✈️", "Lifestyle", "#1ABC9C"),
    Tag("life-food", "Food & Cooking", "🍳", "Lifestyle", "#1ABC9C"),
    Tag("life-fashion", "Fashion & Beauty", "👗", "Lifestyle", "#1ABC9C"),
    Tag("life-home", "Home & DIY", "🔨", "Lifestyle", "#1ABC9C"),
    Tag("life-pets", "Pets & Animals", "🐕", "Lifestyle", "#1ABC9C"),
    Tag("life-relationships", "Relationships & Dating", "💑", "Lifestyle", "#1ABC9C"),
    // Sports
    Tag("sport-team", "Team Sports", "⚽", "Sports", "#E67E22"),
    Tag("sport-individual", "Individual Sports", "🏃", "Sports", "#E67E22"),
    Tag("sport-outdoor", "Outdoor Activities", "🏕️", "Sports", "#E67E22"),
    Tag("sport-esports", "E-Sports", "🕹️", "Sports", "#E67E22"),
    // Social
    Tag("social-activism", "Activism & Causes", "✊", "Social", "#34495E"),
    Tag("social-politics", "Politics & News", "📰", "Social", "#34495E"),
    Tag("social-environment", "Environment & Sustainability", "🌱", "Social", "#34495E"),
    Tag("social-community", "Community & Local", "🤝", "Social", "#34495E"),
    // Entertainment
    Tag("ent-movies", "Movies & TV Shows", "🎥", "Entertainment", "#E91E63"),
    Tag("ent-books", "Books & Reading", "📖", "Entertainment", "#E91E63"),
    Tag("ent-comedy", "Comedy & Humor", "😂", "Entertainment", "#E91E63"),
    Tag("ent-podcasts", "Podcasts & Audio", "🎙️", "Entertainment", "#E91E63"),
    // Spirituality
    Tag("spirit-philosophy", "Philosophy", "🤔", "Spirituality", "#8E44AD"),
    Tag("spirit-religion", "Religion & Faith", "🕉️", "Spirituality", "#8E44AD"),
    Tag("spirit-mindfulness", "Mindfulness & Meditation", "🧘", "Spirituality", "#8E44AD")
)

// ============================================
// TAG CHIP COMPONENTS
// ============================================

@Composable
fun TagChip(
    tag: Tag,
    isSelected: Boolean,
    onClick: () -> Unit,
    enabled: Boolean = true
) {
    FilterChip(
        selected = isSelected,
        onClick = onClick,
        enabled = enabled,
        label = {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text(tag.icon)
                Spacer(Modifier.width(6.dp))
                Text(tag.name, style = MaterialTheme.typography.bodySmall)
            }
        }
    )
}

@Composable
fun SelectedTagChip(
    tag: Tag,
    onRemove: () -> Unit
) {
    Surface(
        color = MaterialTheme.colorScheme.primaryContainer,
        shape = RoundedCornerShape(16.dp)
    ) {
        Row(
            modifier = Modifier.padding(horizontal = 12.dp, vertical = 6.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Text(tag.icon)
            Spacer(Modifier.width(6.dp))
            Text(
                tag.name,
                style = MaterialTheme.typography.labelMedium,
                fontWeight = FontWeight.Medium,
                color = MaterialTheme.colorScheme.onPrimaryContainer
            )
            Spacer(Modifier.width(8.dp))
            IconButton(
                onClick = onRemove,
                modifier = Modifier.size(18.dp)
            ) {
                Icon(
                    Icons.Default.Close,
                    "Remove",
                    modifier = Modifier.size(14.dp),
                    tint = MaterialTheme.colorScheme.onPrimaryContainer
                )
            }
        }
    }
}

// ============================================
// TAG ROW - compact horizontal display
// ============================================

@Composable
fun TagRow(
    tags: List<Tag>,
    onTagClick: (Tag) -> Unit = {}
) {
    LazyRow(
        horizontalArrangement = Arrangement.spacedBy(8.dp),
        contentPadding = PaddingValues(horizontal = 16.dp, vertical = 8.dp)
    ) {
        items(tags) { tag ->
            AssistChip(
                onClick = { onTagClick(tag) },
                label = {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Text(tag.icon)
                        Spacer(Modifier.width(4.dp))
                        Text(tag.name, style = MaterialTheme.typography.labelSmall)
                    }
                }
            )
        }
    }
}

// ============================================
// TAG SELECTOR - full browsable picker
// ============================================

@Composable
fun TagSelector(
    selectedTags: List<String>,
    onTagsChanged: (List<String>) -> Unit,
    maxTags: Int = 5,
    allTags: List<Tag> = UNIVERSAL_TAGS
) {
    var searchQuery by remember { mutableStateOf("") }
    var selectedCategory by remember { mutableStateOf<String?>(null) }
    var expandedCategories by remember { mutableStateOf(setOf<String>()) }

    Column(Modifier.fillMaxWidth()) {
        // Search bar
        OutlinedTextField(
            value = searchQuery,
            onValueChange = { searchQuery = it },
            modifier = Modifier.fillMaxWidth().padding(16.dp),
            placeholder = { Text("Search tags...") },
            leadingIcon = { Icon(Icons.Default.Search, null) },
            trailingIcon = {
                if (searchQuery.isNotEmpty()) {
                    IconButton(onClick = { searchQuery = "" }) {
                        Icon(Icons.Default.Close, "Clear")
                    }
                }
            },
            singleLine = true,
            shape = RoundedCornerShape(24.dp)
        )

        // Selected tags
        if (selectedTags.isNotEmpty()) {
            LazyRow(
                modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 8.dp),
                horizontalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                items(selectedTags) { tagId ->
                    val tag = allTags.find { it.id == tagId }
                    if (tag != null) {
                        SelectedTagChip(
                            tag = tag,
                            onRemove = { onTagsChanged(selectedTags - tagId) }
                        )
                    }
                }
            }
            Text(
                "${selectedTags.size}/$maxTags tags selected",
                modifier = Modifier.padding(horizontal = 16.dp),
                style = MaterialTheme.typography.labelSmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
            HorizontalDivider(Modifier.padding(vertical = 8.dp))
        }

        // Category filter
        LazyRow(
            modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp),
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            item {
                FilterChip(
                    selected = selectedCategory == null,
                    onClick = { selectedCategory = null },
                    label = { Text("All") }
                )
            }
            items(allTags.map { it.category }.distinct().sorted()) { category ->
                FilterChip(
                    selected = selectedCategory == category,
                    onClick = { selectedCategory = category },
                    label = { Text(category) }
                )
            }
        }

        Spacer(Modifier.height(8.dp))

        // Tags by category
        val filteredTags = allTags.filter { tag ->
            (selectedCategory == null || tag.category == selectedCategory) &&
            (searchQuery.isEmpty() || tag.name.contains(searchQuery, ignoreCase = true))
        }
        val groupedTags = filteredTags.groupBy { it.category }

        groupedTags.forEach { (category, tags) ->
            // Category header
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 16.dp, vertical = 4.dp),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    "$category (${tags.size})",
                    style = MaterialTheme.typography.titleSmall,
                    fontWeight = FontWeight.Bold
                )
                IconButton(
                    onClick = {
                        expandedCategories = if (expandedCategories.contains(category)) {
                            expandedCategories - category
                        } else {
                            expandedCategories + category
                        }
                    }
                ) {
                    Icon(
                        if (expandedCategories.contains(category)) Icons.Default.ExpandLess else Icons.Default.ExpandMore,
                        "Toggle"
                    )
                }
            }

            if (expandedCategories.contains(category) || searchQuery.isNotEmpty()) {
                FlowRow(
                    modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 4.dp),
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                    verticalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    tags.forEach { tag ->
                        TagChip(
                            tag = tag,
                            isSelected = selectedTags.contains(tag.id),
                            onClick = {
                                if (selectedTags.contains(tag.id)) {
                                    onTagsChanged(selectedTags - tag.id)
                                } else if (selectedTags.size < maxTags) {
                                    onTagsChanged(selectedTags + tag.id)
                                }
                            },
                            enabled = selectedTags.size < maxTags || selectedTags.contains(tag.id)
                        )
                    }
                }
            }
        }
    }
}

// ============================================
// ONBOARDING TAG SCREEN
// ============================================

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun OnboardingTagScreen(
    onComplete: (List<String>) -> Unit,
    onSkip: () -> Unit
) {
    var selectedTags by remember { mutableStateOf<List<String>>(emptyList()) }

    Scaffold(
        topBar = {
            TopAppBar(
                title = { Text("Choose Your Interests") },
                actions = {
                    TextButton(onClick = onSkip) { Text("Skip") }
                }
            )
        },
        bottomBar = {
            Surface(tonalElevation = 3.dp) {
                Row(
                    modifier = Modifier.fillMaxWidth().padding(16.dp),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text(
                        "${selectedTags.size} selected",
                        style = MaterialTheme.typography.bodyMedium
                    )
                    Button(
                        onClick = { onComplete(selectedTags) },
                        enabled = selectedTags.isNotEmpty()
                    ) {
                        Text("Continue")
                    }
                }
            }
        }
    ) { padding ->
        Column(
            modifier = Modifier.fillMaxSize().padding(padding)
        ) {
            Text(
                "Select at least 3 topics you're interested in to personalize your feed.",
                modifier = Modifier.padding(16.dp),
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
            TagSelector(
                selectedTags = selectedTags,
                onTagsChanged = { selectedTags = it },
                maxTags = 10
            )
        }
    }
}
